// Homes: list, create, detail with members, visits and tickets.
import { api } from "../lib/api";
import { HOME_ROLES, HOME_STATUSES } from "../lib/catalog";
import { fmt, t, tl } from "../lib/i18n";
import { Link, navigate } from "../lib/router";
import { brand } from "../lib/brand";
import { can } from "../lib/session";
import { h, invalidate, local, resource } from "../lib/ui";
import {
  act, Async, Button, centsToInput, Chip, confirmDialog, Dialog, Field, formData, HealthChip, isBusy, LoadError, Loading, openDialog,
  Prio, Select, showLinkDialog, TicketStatus, toast, toCents, Toggle, VisitStatus,
} from "../shared/components";
import { Icon } from "../shared/icons";
import { PageHead } from "./app";
import { shrinkImage } from "../lib/image";
import { newTicketDialog } from "./tickets";
import { DeliveryChoice, showFirstSignIn } from "./users";
import { scheduleVisitDialog } from "./visits";

const STATUS_KIND: Record<string, any> = {
  lead: "neutral", entry_consent: "info", checkup_booked: "info", trial: "accent", active: "good",
  suspended: "attention", pending_transfer: "attention", terminated: "neutral",
};
export function HomeStatus(props: { status: string }) {
  return <Chip kind={STATUS_KIND[props.status]}>{t(`homeStatus.${props.status}`)}</Chip>;
}

export function useRefs() {
  return resource("admin.refs", () => api.get("/api/admin/refs"));
}
type Refs = ReturnType<typeof useRefs>;

function homeForm(home: any | null, refs: Refs, onSaved: (id: string) => void) {
  const experts = refs.data?.experts ?? [];
  const plans: any[] = refs.data?.plans ?? [];
  const areas: string[] = refs.data?.serviceAreas ?? [home?.service_area ?? "West Vancouver"];
  const expertOpts = [{ value: "", label: t("common.none") }, ...experts.map((e: any) => ({ value: e.id, label: e.name }))];
  openDialog((close) => (
    <Dialog title={home ? t("admin.homes.editTitle", { code: home.code }) : t("admin.homes.newTitle")} onClose={close} wide>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        const body: any = {
          nickname: d.nickname, building_type: d.building_type, ownership: d.ownership, address_line1: d.address_line1,
          address_line2: d.address_line2, city: d.city, postal_code: d.postal_code, service_area: d.service_area, status: d.status,
          plan: d.plan, primary_expert_id: d.primary_expert_id, backup_expert_id: d.backup_expert_id,
          emergency_cap_cents: toCents(d.emergency_cap) ?? 0, notes: d.notes,
        };
        act("home.save", async () => {
          const res = home ? await api.patch(`/api/admin/homes/${home.id}`, body) : await api.post("/api/admin/homes", body);
          close();
          invalidate("admin.home");
          onSaved(home?.id ?? res.id);
        }, t("common.saved"));
      }}>
        <div class="cols-2">
          <Field label={t("fields.address_line1")} id="h-a1"><input class="input" id="h-a1" name="address_line1" required value={home?.address_line1 ?? ""} /></Field>
          <Field label={t("fields.address_line2")} id="h-a2"><input class="input" id="h-a2" name="address_line2" value={home?.address_line2 ?? ""} /></Field>
        </div>
        <div class="cols-2">
          <Field label={t("fields.city")} id="h-city"><input class="input" id="h-city" name="city" required value={home?.city ?? "West Vancouver"} /></Field>
          <Field label={t("fields.postal_code")} id="h-pc" hint={t("admin.homes.postalHint")}><input class="input" id="h-pc" name="postal_code" value={home?.postal_code ?? ""} autocapitalize="characters" maxLength={8} placeholder="V7V 1L2" /></Field>
        </div>
        <div class="cols-2">
          <Field label={t("fields.nickname")} id="h-nick" hint={t("admin.homes.nicknameHint")}><input class="input" id="h-nick" name="nickname" value={home?.nickname ?? ""} /></Field>
          <Field label={t("fields.service_area")} id="h-area"><Select id="h-area" name="service_area" value={home?.service_area ?? areas[0]} options={areas.map((a) => ({ value: a, label: a }))} /></Field>
        </div>
        <div class="cols-2">
          <Field label={t("fields.building_type")} id="h-bt">
            <Select id="h-bt" name="building_type" value={home?.building_type ?? "villa"} options={[{ value: "villa", label: t("buildingType.villa") }, { value: "strata_unit", label: t("buildingType.strata_unit") }]} />
          </Field>
          <Field label={t("fields.ownership")} id="h-own">
            <Select id="h-own" name="ownership" value={home?.ownership ?? "owner"} options={["owner", "legal_authority", "estate"].map((o) => ({ value: o, label: t(`ownership.${o}`) }))} />
          </Field>
        </div>
        <div class="cols-2">
          <Field label={t("fields.status")} id="h-st"><Select id="h-st" name="status" value={home?.status ?? "lead"} options={HOME_STATUSES.map((s) => ({ value: s, label: t(`homeStatus.${s}`) }))} /></Field>
          <Field label={t("fields.plan")} id="h-plan"><Select id="h-plan" name="plan" value={home?.plan ?? ""} options={[{ value: "", label: t("common.none") }, ...plans.filter((p) => p.active || p.key === home?.plan).map((p) => ({ value: p.key, label: `${tl(p.name)} · ${fmt.money(p.priceCents)}` }))]} /></Field>
        </div>
        <div class="cols-2">
          <Field label={t("fields.primary_expert_id")} id="h-pe"><Select id="h-pe" name="primary_expert_id" value={home?.primary_expert_id ?? ""} options={expertOpts} /></Field>
          <Field label={t("fields.backup_expert_id")} id="h-be"><Select id="h-be" name="backup_expert_id" value={home?.backup_expert_id ?? ""} options={expertOpts} /></Field>
        </div>
        <Field label={t("fields.emergency_cap_cents")} id="h-cap" hint={home && !can("admin.settings") ? t("admin.homes.capLocked") : t("admin.homes.capHint")}>
          <div class="input-affix"><span class="affix">$</span><input class="input tabular" id="h-cap" name="emergency_cap" inputMode="decimal" readOnly={!!home && !can("admin.settings")} value={centsToInput(home?.emergency_cap_cents ?? refs.data?.emergencyCapDefault ?? 30000)} /></div>
        </Field>
        <Field label={t("fields.notes")} id="h-notes" hint={t("admin.homes.notesHint")}><textarea class="textarea" id="h-notes" name="notes" value={home?.notes ?? ""} /></Field>
        <div class="row end">
          <Button kind="secondary" onClick={close}>{t("common.cancel")}</Button>
          <Button type="submit" busy={isBusy("home.save")}>{home ? t("common.save") : t("admin.homes.create")}</Button>
        </div>
      </form>
    </Dialog>
  ));
}

export function HomesPage() {
  const s = local("screen:homes", () => ({ status: "", q: "" }));
  const key = `admin.home.list.${s.status}.${s.q}`;
  const r = resource(key, () => api.get(`/api/admin/homes?status=${encodeURIComponent(s.status)}&q=${encodeURIComponent(s.q)}`));
  const refs = useRefs();
  let timer: any;
  return (
    <div class="stack loose">
      <PageHead title={t("admin.homes.title")} intro={t("admin.homes.intro")}
        actions={<Button icon="plus" onClick={() => homeForm(null, refs, (id) => navigate(`/admin/homes/${id}`))}>{t("admin.homes.new")}</Button>} />
      <section class="panel">
        <div class="panel-head" style="flex-wrap:wrap">
          <div class="input-affix" style="max-width:340px;flex:1">
            <span class="affix"><Icon name="search" size={16} /></span>
            <input class="input" type="search" placeholder={t("admin.homes.search")} value={s.q} aria-label={t("admin.homes.search")}
              onInput={(e: Event) => { const v = (e.target as HTMLInputElement).value; clearTimeout(timer); timer = setTimeout(() => s.set({ q: v }), 250); }} />
          </div>
          <div style="width:220px">
            <Select value={s.status} onChange={(v) => s.set({ status: v })} options={[{ value: "", label: t("admin.homes.allStatuses") }, ...HOME_STATUSES.map((x) => ({ value: x, label: t(`homeStatus.${x}`) }))]} />
          </div>
        </div>
        {Async(r, (rows: any[]) => rows.length ? (
          <div class="table-wrap">
            <table class="table">
              <thead><tr><th>{t("fields.home")}</th><th>{t("fields.status")}</th><th>{t("fields.health")}</th><th>{t("fields.account_holder")}</th><th>{t("fields.primary_expert_id")}</th><th class="right">{t("admin.homes.openTickets")}</th></tr></thead>
              <tbody>
                {rows.map((x) => (
                  <tr class="link" onClick={() => navigate(`/admin/homes/${x.id}`)}>
                    <td>
                      <div class="row" style="gap:12px;flex-wrap:nowrap">
                        {x.photo_file_id
                          ? <img class="home-thumb" src={`/api/files/${x.photo_file_id}`} alt="" loading="lazy" />
                          : <span class="home-thumb empty"><Icon name="home" size={18} /></span>}
                        <div>
                          <span class="strong">{x.address_line1}</span>
                          {x.access_revoked_at ? <span class="chip critical" style="margin-left:8px">{t("admin.homes.accessRevoked")}</span> : null}
                          <span class="sub">{x.code} · {t(`buildingType.${x.building_type}`)}{x.occupancy !== "occupied" ? ` · ${t(`occupancy.${x.occupancy}`)}` : ""}</span>
                        </div>
                      </div>
                    </td>
                    <td><HomeStatus status={x.status} /></td>
                    <td><HealthChip health={x.health} /></td>
                    <td>{x.holder ?? <span class="muted">—</span>}</td>
                    <td>{x.primary_expert ?? <span class="muted">—</span>}</td>
                    <td class="right tabular">{x.open_tickets || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div class="empty"><strong>{s.q || s.status ? t("admin.homes.noMatch") : t("admin.homes.emptyTitle")}</strong><span>{t("admin.homes.emptyBody")}</span></div>
        ), 6)}
      </section>
    </div>
  );
}

function memberDialog(homeId: string, m: any | null, hasHolder: boolean) {
  const isHolder = !!m?.roles.includes("account_holder");
  const locked = isHolder || m?.status === "revoked";
  const removeAccess = async (close: () => void) => {
    if (!(await confirmDialog({ title: t("customer.people.removeTitle"), body: t("customer.people.removeBody", { name: m.name }), confirm: t("customer.people.remove"), danger: true }))) return;
    act("member.revoke", async () => {
      await api.patch(`/api/admin/memberships/${m.id}`, { status: "revoked" });
      close();
      invalidate(`admin.home.${homeId}`);
    }, t("admin.homes.accessRemoved"));
  };
  openDialog((close) => (
    <Dialog title={m ? m.name : t("admin.homes.addMember")} onClose={close}>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        const roles = HOME_ROLES.filter((r) => d[`role_${r}`]);
        act("member.save", async () => {
          if (m) {
            await api.patch(`/api/admin/memberships/${m.id}`, { roles });
            close();
            toast(t("common.saved"));
          } else {
            const res = await api.post(`/api/admin/homes/${homeId}/members`, {
              name: d.name, email: d.email, phone: d.phone, locale: d.locale, roles,
              delivery: d.delivery ?? "link", ...(d.delivery === "temp_password" && d.tempPassword ? { tempPassword: d.tempPassword } : {}),
            });
            close();
            showFirstSignIn(res, d.name, d.email, d.delivery === "temp_password" ? d.tempPassword : null);
          }
          invalidate(`admin.home.${homeId}`);
        });
      }}>
        {!m ? (
          <div class="stack">
            <div class="cols-2">
              <Field label={t("fields.name")} id="m-name"><input class="input" id="m-name" name="name" required /></Field>
              <Field label={t("fields.email")} id="m-email"><input class="input" id="m-email" name="email" type="email" required /></Field>
            </div>
            <div class="cols-2">
              <Field label={t("fields.phone")} id="m-phone"><input class="input" id="m-phone" name="phone" type="tel" /></Field>
              <Field label={t("fields.locale")} id="m-loc"><Select id="m-loc" name="locale" value="en" options={[{ value: "en", label: t("languages.en") }, { value: "fr", label: t("languages.fr") }]} /></Field>
            </div>
          </div>
        ) : null}
        {locked ? (
          <div class="note small">{isHolder ? t("admin.homes.holderLocked") : t("admin.homes.revokedLocked")}</div>
        ) : (
          <fieldset class="stack tight" style="border:0;padding:0;margin:0">
            <legend class="label" style="margin-bottom:6px">{t("admin.homes.roles")}</legend>
            {HOME_ROLES.map((r) => {
              // The account holder is set once at onboarding; afterwards only the transfer process changes it.
              const holderBox = r === "account_holder";
              const disabled = holderBox && (!!m || hasHolder);
              return (
                <label class="check" style={disabled ? "opacity:.55" : ""}>
                  <input type="checkbox" name={`role_${r}`} disabled={disabled}
                    checked={m ? m.roles.includes(r) : holderBox ? !hasHolder : r === "remote" && hasHolder} />
                  <span class="stack tight" style="gap:0"><span class="strong">{t(`homeRole.${r}`)}</span><span class="small muted">{t(`homeRoleHelp.${r}`)}</span></span>
                </label>
              );
            })}
          </fieldset>
        )}
        {!m ? <DeliveryChoice id="member" /> : null}
        {m && !locked ? (
          <div class="note small stack tight">
            <span>{t("admin.homes.holderDecides")}</span>
            <span class="muted">
              {t("admin.homes.approvalLimit")}: {m.approval_limit_cents !== null ? fmt.money(m.approval_limit_cents) : t("admin.homes.roleDefault")}
              {m.can_view_costs ? ` · ${t("admin.homes.remoteViewCosts")}` : ""}
              {m.can_approve_costs ? ` · ${t("admin.homes.remoteApproveCosts")}` : ""}
            </span>
            <span><Icon name="video" size={14} /> {m.bodycam_access ? t("admin.homes.bodycamGranted") : t("admin.homes.bodycamNotGranted")}</span>
          </div>
        ) : null}
        <div class="row between">
          <div class="row">
            {m && !m.has_password && m.user_status !== "disabled" && m.status !== "revoked" && brand.current.emailEnabled ? (
              <Button kind="ghost" icon="mail" onClick={() => act("member.mail", async () => {
                const res = await api.post(`/api/admin/users/${m.user_id}/send-email`);
                toast(t("admin.users.inviteSent", { email: res.to }));
              })}>{t("admin.users.sendInvite")}</Button>
            ) : null}
            {m && !m.has_password && m.user_status !== "disabled" && m.status !== "revoked" && can("admin.users") ? (
              <Button kind="ghost" icon="link" onClick={() => act("member.link", async () => {
                const res = await api.post(`/api/admin/users/${m.user_id}/link`);
                showLinkDialog(t("admin.homes.inviteLinkTitle"), res.url, t("admin.homes.inviteLinkBody", { name: m.name }));
              })}>{t("admin.homes.newInviteLink")}</Button>
            ) : null}
            {m && !locked ? <Button kind="ghost" onClick={() => removeAccess(close)}>{t("customer.people.remove")}</Button> : null}
          </div>
          <div class="row">
            <Button kind="secondary" onClick={close}>{locked ? t("common.close") : t("common.cancel")}</Button>
            {!locked ? <Button type="submit" busy={isBusy("member.save")}>{m ? t("common.save") : t("admin.homes.addAndInvite")}</Button> : null}
          </div>
        </div>
      </form>
    </Dialog>
  ));
}

/** The photo of the building at the top of the home's summary, with add/replace/remove. */
function HomePhoto(props: { home: any; reload: () => void }) {
  const home = props.home;
  const pick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/webp";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      act("home.photo", async () => {
        const fd = new FormData();
        fd.append("photo", await shrinkImage(file), "home.jpg");
        await api.upload(`/api/admin/homes/${home.id}/photo`, fd);
        props.reload();
        invalidate("admin.homes");
      }, t("admin.homes.photoSaved"));
    };
    input.click();
  };
  const remove = async () => {
    if (!(await confirmDialog({ title: t("admin.homes.photoRemoveTitle"), body: t("admin.homes.photoRemoveBody"), confirm: t("admin.homes.photoRemove"), danger: true }))) return;
    act("home.photo", async () => {
      await api.del(`/api/admin/homes/${home.id}/photo`);
      props.reload();
      invalidate("admin.homes");
    });
  };
  const canEdit = can("ops.homes");
  return home.photo_file_id ? (
    <figure class="home-photo-block">
      <img class="home-photo" src={`/api/files/${home.photo_file_id}`} alt={t("admin.homes.photoAlt", { address: home.address_line1 })} />
      {canEdit ? (
        <figcaption class="row" style="gap:6px">
          <Button kind="secondary" size="small" icon="camera" busy={isBusy("home.photo")} onClick={pick}>{t("admin.homes.photoReplace")}</Button>
          <Button kind="ghost" size="small" onClick={remove}>{t("admin.homes.photoRemove")}</Button>
        </figcaption>
      ) : null}
    </figure>
  ) : canEdit ? (
    <button type="button" class="home-photo-empty" onClick={pick} disabled={isBusy("home.photo")}>
      <Icon name="camera" size={22} />
      <span class="strong">{t("admin.homes.photoAdd")}</span>
      <span class="small muted">{t("admin.homes.photoHint")}</span>
    </button>
  ) : null;
}

export function HomeDetail(props: { id: string }) {
  const r = resource(`admin.home.${props.id}`, () => api.get(`/api/admin/homes/${props.id}`));
  const refs = useRefs();
  if (r.error && !r.data) return <LoadError error={r.error} retry={() => r.reload()} />;
  if (!r.data) return <Loading rows={8} />;
  const { home, members, visits, tickets, videoCount } = r.data;
  const plan = refs.data?.plans?.find((p: any) => p.key === home.plan);
  const expertName = (id: string | null) => refs.data?.experts?.find((e: any) => e.id === id)?.name ?? "—";
  const toggleHold = (v: boolean) => act("home.hold", async () => {
    await api.patch(`/api/admin/homes/${home.id}`, { dispute_hold: v });
    r.reload();
  }, v ? t("admin.homes.holdOn") : t("admin.homes.holdOff"));
  const followup = () => openDialog((close) => (
    <Dialog title={t("admin.homes.followupTitle")} onClose={close}>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        act("home.followup", async () => { await api.post(`/api/admin/homes/${home.id}/killswitch-followup`, { note: d.note }); close(); }, t("admin.homes.followupSaved"));
      }}>
        <p class="small muted">{t("admin.homes.followupBody")}</p>
        <Field label={t("fields.note")} id="fu-note"><textarea class="textarea" id="fu-note" name="note" required /></Field>
        <div class="row end"><Button kind="secondary" onClick={close}>{t("common.cancel")}</Button><Button type="submit" busy={isBusy("home.followup")}>{t("admin.homes.logCall")}</Button></div>
      </form>
    </Dialog>
  ));

  return (
    <div class="stack loose">
      <PageHead
        crumbs={[{ href: "/admin/homes", label: t("admin.homes.title") }]}
        title={home.nickname || home.address_line1}
        intro={`${home.code} · ${home.address_line1}${home.address_line2 ? `, ${home.address_line2}` : ""}, ${home.city}`}
        actions={[
          <HomeStatus status={home.status} />,
          <HealthChip health={home.health} />,
          <Button kind="secondary" icon="sliders" onClick={() => homeForm(home, refs, () => r.reload())}>{t("common.edit")}</Button>,
        ]}
      />
      {home.access_revoked_at ? (
        <div class="alert-strip" role="alert">
          <Icon name="power" />
          <div class="stack tight grow">
            <strong>{t("admin.homes.revokedTitle", { when: fmt.dateTime(home.access_revoked_at) })}</strong>
            <span class="small">{t("admin.homes.revokedBody")}</span>
          </div>
          <Button kind="danger" size="small" icon="phone" onClick={followup}>{t("admin.homes.logCall")}</Button>
        </div>
      ) : null}

      <div class="cols-2">
        <section class="panel">
          <div class="panel-head"><h2>{t("admin.homes.summary")}</h2></div>
          <div class="panel-body">
            <HomePhoto home={home} reload={() => r.reload()} />
            <dl class="stack tight" style="margin:0">
              {[
                [t("fields.building_type"), t(`buildingType.${home.building_type}`)],
                [t("fields.plan"), plan ? `${tl(plan.name)} · ${fmt.money(plan.priceCents)}` : "—"],
                [t("fields.primary_expert_id"), expertName(home.primary_expert_id)],
                [t("fields.backup_expert_id"), expertName(home.backup_expert_id)],
                [t("fields.occupancy"), `${t(`occupancy.${home.occupancy}`)} · ${t("admin.homes.since", { date: fmt.date(home.occupancy_since) })}`],
                [t("fields.emergency_cap_cents"), fmt.money(home.emergency_cap_cents)],
                [t("fields.ownership"), t(`ownership.${home.ownership}`)],
              ].map(([k, v]) => (
                <div class="row between" style="gap:12px"><dt class="muted small">{k}</dt><dd style="margin:0;text-align:right" class="small strong">{v}</dd></div>
              ))}
            </dl>
            {home.notes ? <p class="note small" style="margin-top:14px;white-space:pre-wrap">{home.notes}</p> : null}
          </div>
        </section>

        <section class="panel">
          <div class="panel-head"><h2>{t("admin.homes.privacy")}</h2></div>
          <div class="panel-body stack">
            <div class="row between">
              <div class="stack tight" style="gap:2px">
                <span class="strong">{t("admin.homes.disputeHold")}</span>
                <span class="small muted">{t("admin.homes.disputeHoldHelp")}</span>
              </div>
              <Toggle checked={home.dispute_hold} onChange={toggleHold} />
            </div>
            <div class="divider" />
            <div class="row between">
              <span class="small">{t("admin.homes.videosStored", { count: videoCount })}</span>
              <Chip kind="info" plain><Icon name="lock" size={14} /> {t("admin.homes.ownerOnly")}</Chip>
            </div>
            <p class="xs muted">{t("admin.homes.videoPolicy")}</p>
          </div>
        </section>
      </div>

      <section class="panel">
        <div class="panel-head">
          <h2>{t("admin.homes.people")}</h2>
          {can("ops.members") ? <Button size="small" icon="plus" onClick={() => memberDialog(home.id, null, members.some((x: any) => x.roles.includes("account_holder") && x.status !== "revoked"))}>{t("admin.homes.addMember")}</Button> : null}
        </div>
        {members.length ? (
          <div class="table-wrap">
            <table class="table">
              <thead><tr><th>{t("fields.name")}</th><th>{t("admin.homes.roles")}</th><th>{t("admin.homes.approvalLimit")}</th><th>{t("fields.status")}</th></tr></thead>
              <tbody>
                {members.map((m: any) => (
                  <tr class={can("ops.members") ? "link" : ""} onClick={() => can("ops.members") && memberDialog(home.id, m, true)}>
                    <td><span class="strong">{m.name}</span><span class="sub">{m.email}{m.phone ? ` · ${m.phone}` : ""}</span></td>
                    <td><div class="row" style="gap:6px">{m.roles.map((x: string) => <Chip kind="info" plain>{t(`homeRole.${x}`)}</Chip>)}</div></td>
                    <td class="tabular small">{m.roles.includes("account_holder") ? t("admin.homes.unlimited") : m.approval_limit_cents !== null ? fmt.money(m.approval_limit_cents) : t("admin.homes.roleDefault")}</td>
                    <td>
                      <Chip kind={m.status === "active" ? "good" : m.status === "revoked" ? "neutral" : "accent"}>{t(`memberStatus.${m.status}`)}</Chip>
                      {!m.has_password && m.status !== "revoked" ? <span class="sub">{t("admin.homes.notSignedUp")}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div class="empty"><strong>{t("admin.homes.noPeople")}</strong><span>{t("admin.homes.noPeopleBody")}</span></div>}
      </section>

      <div class="cols-2">
        <section class="panel">
          <div class="panel-head">
            <h2>{t("admin.homes.visits")}</h2>
            {can("ops.visits") ? <Button size="small" kind="secondary" icon="calendar" onClick={() => scheduleVisitDialog(home, () => r.reload())}>{t("admin.visits.schedule")}</Button> : null}
          </div>
          {visits.length ? (
            <table class="table"><tbody>
              {visits.slice(0, 8).map((v: any) => (
                <tr>
                  <td><span class="strong">{t(`visitType.${v.type}`)}</span><span class="sub">{fmt.dateTime(v.scheduled_for)} · {v.expert ?? "—"}</span></td>
                  <td class="right">{v.outcome ? <HealthChip health={v.outcome} /> : <VisitStatus status={v.status} />}</td>
                </tr>
              ))}
            </tbody></table>
          ) : <div class="empty"><span>{t("admin.homes.noVisits")}</span></div>}
        </section>
        <section class="panel">
          <div class="panel-head">
            <h2>{t("admin.homes.tickets")}</h2>
            {can("ops.tickets") ? <Button size="small" kind="secondary" icon="plus" onClick={() => newTicketDialog(home.id, (id) => navigate(`/admin/tickets/${id}`))}>{t("admin.tickets.new")}</Button> : null}
          </div>
          {tickets.length ? (
            <table class="table"><tbody>
              {tickets.slice(0, 8).map((x: any) => (
                <tr class="link" onClick={() => navigate(`/admin/tickets/${x.id}`)}>
                  <td style="width:44px"><Prio p={x.priority} /></td>
                  <td><span class="strong">{x.title}</span><span class="sub">#{x.number} · {t(`service.${x.service}`)}</span></td>
                  <td class="right"><TicketStatus status={x.status} /></td>
                </tr>
              ))}
            </tbody></table>
          ) : <div class="empty"><span>{t("admin.homes.noTickets")}</span></div>}
        </section>
      </div>
      <p class="xs muted"><Link href={`/admin/audit?home=${home.id}`}>{t("admin.homes.auditLink")}</Link></p>
    </div>
  );
}

